import type { LiveDocumentEditingConfig } from '@/document-render/presentation/editing/quote/LiveDocumentEditingConfig';
import { useQuoteCanvasEditing } from '@/document-render/presentation/editing/quote/QuoteCanvasEditingContext';

const PRICE_BOOK_CALLBACKS = [
  'onEditPriceBookCell',
  'onReorderPriceBookRows',
  'onSelectPriceBookField',
  'onDuplicatePriceBookSheet',
  'onDeletePriceBookSheet',
  'onEditPriceBookSheetName',
  'onTogglePriceBookRowHidden',
  'onDuplicatePriceBookRow',
  'onDeletePriceBookRow',
  'onReorderPriceBookLineItems'
] as const satisfies ReadonlyArray<keyof LiveDocumentEditingConfig>;

export interface PriceBookCanvas {
  editing: LiveDocumentEditingConfig;
  selectedId: string | null;
  select: (id: string | null) => void;
}

export function hasPriceBookCallbacks(editing: LiveDocumentEditingConfig): boolean {
  return PRICE_BOOK_CALLBACKS.some((key) => typeof editing[key] === 'function');
}

export function priceBookSheetSelectionId(sheetId: string): string {
  return `pb-sheet-${sheetId}`;
}

export function priceBookRowSelectionId(rowId: string): string {
  return `pb-row-${rowId}`;
}

/** The customised price-book canvas; `null` before the host wires any price-book callback. */
export function usePriceBookCanvas(): PriceBookCanvas | null {
  const slice = useQuoteCanvasEditing();
  if (slice?.kind !== 'priceBook' || !hasPriceBookCallbacks(slice.editing)) return null;
  const { editing } = slice;
  return {
    editing,
    selectedId: editing.selectedPriceBookFieldId ?? null,
    select: (id) => editing.onSelectPriceBookField?.(id)
  };
}
